import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import toast, { Toaster } from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import {ArrowLeft, Loader2, Lock, LogIn, Mail} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'

function AdminLogin() {
  const navigate = useNavigate()
  const { isAuthenticated, isAdmin } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Se já estiver autenticado como admin, redireciona para /admin
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      navigate('/admin', { replace: true })
    }
  }, [isAuthenticated, isAdmin, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return

    if (!email.trim()) {
      toast.error('Informe seu e-mail.')
      return
    }
    if (!password) {
      toast.error('Informe sua senha.')
      return
    }

    setSubmitting(true)
    const loadingId = toast.loading('Autenticando...')

    try {
      await lumi.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      })

      // Após login, valida se o usuário é ADMIN
      const role = lumi.auth.user?.userRole
      if (role !== 'ADMIN') {
        await lumi.auth.signOut()
        toast.dismiss(loadingId)
        toast.error('Esta conta não tem permissão de administrador.')
        setSubmitting(false)
        return
      }

      toast.dismiss(loadingId)
      toast.success('Bem-vindo!')
      window.location.href = '/admin'
    } catch (error: any) {
      console.error('Login failed:', error)
      toast.dismiss(loadingId)
      const code = error?.data?.code
      if (code === 2259) {
        toast.error('Conta inativa. Contate o suporte.')
      } else if (code === 2261) {
        toast.error('Login por e-mail desativado.')
      } else {
        toast.error('E-mail ou senha incorretos.')
      }
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 relative overflow-hidden">
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#ffffff', color: '#0F172A', border: '1px solid #E2E8F0', boxShadow: '0 10px 30px rgba(15,23,42,0.08)' },
        }}
      />

      {/* Background light orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-32 w-[28rem] h-[28rem] bg-sky-300/40 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-40 w-[26rem] h-[26rem] bg-teal-300/40 rounded-full blur-3xl" />
      </div>

      <main className="relative z-10 max-w-md mx-auto px-5 pt-12 pb-16 min-h-screen flex flex-col">
        {/* Header com logo */}
        <header className="flex justify-center mb-10">
          <img
            src="https://static.lumi.new/material/16/16579d03c8ed6c5cc8f848176b8adb09.png"
            alt="EW Pass"
            className="h-14 w-auto object-contain"
          />
        </header>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 text-center"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-teal-500 text-white shadow-lg shadow-sky-500/25 mb-4">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2">
            Área <span className="bg-gradient-to-r from-sky-500 to-teal-500 bg-clip-text text-transparent">Administrativa</span>
          </h1>
          <p className="text-slate-600 text-sm">
            Faça login para gerenciar vouchers da campanha.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl border border-slate-200 rounded-2xl p-5 space-y-4 shadow-xl shadow-sky-500/5"
        >
          <label className="block">
            <span className="text-xs font-medium text-slate-700 mb-1.5 block">
              E-mail<span className="text-sky-500 ml-0.5">*</span>
            </span>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@ewpass.com"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 focus:bg-white transition"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-700 mb-1.5 block">
              Senha<span className="text-sky-500 ml-0.5">*</span>
            </span>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 focus:bg-white transition"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-sky-500/25"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Entrando...
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                Entrar
              </>
            )}
          </button>
        </motion.form>

        <div className="flex justify-center mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            Voltar para o site
          </Link>
        </div>

        <footer className="text-center text-slate-400 text-xs mt-auto pt-12">
          © {new Date().getFullYear()} Landing EW Pass.
        </footer>
      </main>
    </div>
  )
}

export default AdminLogin
